/**
 * Safe math evaluator for KPI formulas. Mirrors backend formulaEvaluator:
 * + - * / ( ) % and variable names. Unknown vars and division-by-zero → 0.
 */

const toFloat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const SAFE_FORMULA_REGEX = /^[a-zA-Z0-9_+\-*/().% ,]+$/;
const RESERVED = new Set([
  'AVG', 'SUM', 'MAX', 'MIN', 'ROUND',
  'and', 'or', 'not', 'true', 'false', 'return', 'Math',
]);

export function extractFormulaVariables(formula = '') {
  const matches = String(formula).match(/\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g) || [];
  return [...new Set(matches)].filter((name) => !RESERVED.has(name));
}

function protectDivision(expression) {
  return expression.replace(
    /(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/g,
    (_, a, b) => (Number(b) === 0 ? '0' : `(${a}/${b})`),
  );
}

export function evaluateFormula(formula, variables = {}) {
  if (!formula || typeof formula !== 'string' || !formula.trim()) return 0;

  let expression = formula.trim();
  expression = expression.replace(
    /(\b[a-zA-Z_][a-zA-Z0-9_]*|\d+(?:\.\d+)?)%/g,
    '($1/100)',
  );

  if (!SAFE_FORMULA_REGEX.test(expression)) return 0;

  const keys = Object.keys(variables).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const regex = new RegExp(`\\b${key}\\b`, 'g');
    expression = expression.replace(regex, String(toFloat(variables[key])));
  }

  expression = expression.replace(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, '0');
  expression = protectDivision(expression);

  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`'use strict'; return (${expression})`)();
    if (typeof result !== 'number' || Number.isNaN(result) || !Number.isFinite(result)) {
      return 0;
    }
    return result;
  } catch {
    return 0;
  }
}

export function evaluateFormulaSafe(formula, variables = {}, fallback = 0) {
  if (!formula || !String(formula).trim()) return fallback;
  try {
    return evaluateFormula(formula, variables);
  } catch {
    return fallback;
  }
}
